import { forwardRef } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { gr } from "../tokens";

// One teal `primary` per screen. Urgency is shown by status pills, never by button colour.
const VARIANTS = {
  primary: {
    // brandStrong: white text on it passes AA (brand #04abbb was 2.8:1).
    bg: gr.color.brandStrong,
    fg: gr.color.onBrand,
    border: gr.color.brandStrong,
    hoverBg: gr.color.brandDark,
    hoverBorder: gr.color.brandDark,
    hoverFg: gr.color.onBrand,
    disabledBg: gr.color.line2,
    disabledBorder: gr.color.line2,
  },
  secondary: {
    bg: gr.color.surface,
    fg: gr.color.ink2,
    border: gr.color.line,
    hoverBg: gr.color.surface,
    hoverBorder: gr.color.brand,
    hoverFg: gr.color.brandDark,
    disabledBg: gr.color.surface,
    disabledBorder: gr.color.line,
  },
  // Rare, key action on a list card (Accept Request) or a panel's create action: brand-soft fill + teal text.
  // Lighter than primary, so a screen still has at most one solid teal button.
  soft: {
    bg: gr.color.brandSoft,
    fg: gr.color.brandDark,
    border: gr.color.brandSoft,
    hoverBg: gr.color.brand,
    hoverBorder: gr.color.brand,
    hoverFg: gr.color.onBrand,
    disabledBg: gr.color.line2,
    disabledBorder: gr.color.line2,
  },
  // Action repeated on every card (Chat, Add Friend) or navigation (Friends): teal outline + teal text, not filled,
  // so a screen still has at most one filled primary.
  outline: {
    bg: gr.color.surface,
    fg: gr.color.brandDark,
    border: gr.color.brand,
    hoverBg: gr.color.tint,
    hoverBorder: gr.color.brandDark,
    hoverFg: gr.color.brandDark,
    disabledBg: gr.color.surface,
    disabledBorder: gr.color.line,
  },
  // Cancel / dismiss: still a real outline button, with quieter text.
  quiet: {
    bg: gr.color.surface,
    fg: gr.color.ink2,
    border: gr.color.line,
    hoverBg: gr.color.bg,
    hoverBorder: gr.color.lineStrong,
    hoverFg: gr.color.ink,
    weight: gr.font.weight.medium,
    disabledBg: gr.color.surface,
    disabledBorder: gr.color.line,
  },
  text: {
    bg: "transparent",
    fg: gr.color.brandDark,
    border: "transparent",
    hoverBg: gr.color.tint,
    hoverBorder: "transparent",
    hoverFg: gr.color.brandDark,
    disabledBg: "transparent",
    disabledBorder: "transparent",
  },
  // Inline text link ("See all →", "Forgot password?"): regular weight, underline on hover.
  link: {
    bg: "transparent",
    fg: gr.color.brandDark,
    border: "transparent",
    hoverBg: "transparent",
    hoverBorder: "transparent",
    hoverFg: gr.color.brandDark,
    weight: gr.font.weight.regular,
    disabledBg: "transparent",
    disabledBorder: "transparent",
  },
  danger: {
    bg: gr.color.surface,
    fg: gr.color.danger,
    border: gr.color.line,
    hoverBg: gr.color.dangerSoft,
    hoverBorder: gr.color.danger,
    hoverFg: gr.color.danger,
    disabledBg: gr.color.surface,
    disabledBorder: gr.color.line,
  },
};

const SIZES = {
  sm: {
    height: gr.control.sm,
    padX: gr.space[3],
    font: gr.font.size.sm,
    icon: 16,
  },
  md: {
    height: gr.control.md,
    padX: gr.space[4],
    font: gr.font.size.md,
    icon: 18,
  },
  lg: {
    height: gr.control.lg,
    padX: gr.space[6],
    font: gr.font.size.lg,
    icon: 20,
  },
};

const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";

const Root = styled(Button, {
  shouldForwardProp: (prop) => prop !== "grVariant" && prop !== "grSize",
})(({ grVariant, grSize }) => {
  const v = VARIANTS[grVariant] || VARIANTS.primary;
  const s = SIZES[grSize] || SIZES.md;
  return {
    minHeight: s.height,
    padding: `0 ${s.padX}`,
    gap: gr.space[2],
    minWidth: 0,
    borderRadius: gr.radius.control,
    border: `1px solid ${v.border}`,
    background: v.bg,
    color: v.fg,
    fontFamily: gr.font.family,
    fontSize: s.font,
    fontWeight: v.weight || gr.font.weight.semibold,
    lineHeight: 1.2,
    letterSpacing: 0,
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    transition:
      "background-color .15s ease, border-color .15s ease, color .15s ease",
    "&:hover": {
      background: v.hoverBg,
      borderColor: v.hoverBorder,
      color: v.hoverFg,
      boxShadow: "none",
    },
    "&.Mui-focusVisible": {
      boxShadow: gr.shadow.focus,
      borderColor: gr.color.brand,
    },
    "&.Mui-disabled": {
      background: v.disabledBg,
      borderColor: v.disabledBorder,
      color: gr.color.muted,
    },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
    "& .MuiButton-startIcon > svg, & .MuiButton-endIcon > svg": {
      width: s.icon,
      height: s.icon,
    },
    // Touch screens: md → 44, sm → 40 (comfortable tap targets).
    ...(grVariant !== "link" && grSize !== "lg"
      ? {
          [TOUCH]: {
            minHeight: grSize === "sm" ? gr.control.md : gr.control.touch,
          },
        }
      : {}),
    ...(grVariant === "link"
      ? {
          minHeight: 0,
          padding: 0,
          border: 0,
          borderRadius: gr.radius.chip,
          fontSize: grSize === "lg" ? gr.font.size.md : gr.font.size.sm,
          justifyContent: "flex-start",
          "&:hover": {
            background: "transparent",
            color: v.hoverFg,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
          },
          // Bigger tap target on touch screens without moving the layout.
          [TOUCH]: {
            paddingTop: gr.space[3],
            paddingBottom: gr.space[3],
            marginTop: `calc(-1 * ${gr.space[3]})`,
            marginBottom: `calc(-1 * ${gr.space[3]})`,
          },
        }
      : {}),
  };
});

const GrButton = forwardRef(function GrButton(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    startIcon,
    children,
    ...rest
  },
  ref
) {
  return (
    <Root
      ref={ref}
      grVariant={variant}
      grSize={size}
      variant="text"
      color="inherit"
      disableElevation
      disableRipple
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      startIcon={
        loading ? (
          <CircularProgress size={16} thickness={5} color="inherit" />
        ) : (
          startIcon
        )
      }
      {...rest}
    >
      {children}
    </Root>
  );
});

GrButton.propTypes = {
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "soft",
    "outline",
    "quiet",
    "text",
    "link",
    "danger",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  children: PropTypes.node,
};

export default GrButton;
